import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileText, X } from 'lucide-react';
import { RequestError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Button, ErrorNotice, Field, Input, Spinner } from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { shrinkPicked, titleOf } from '../../components/QuickScan.js';

/**
 * Section 9 (Nitzan, 05.10) — several files picked together are one document.
 *
 * Unlike the invoices, where every file is an invoice of its own: in "המסמכים
 * שלי" a contract, its annex and a photograph of the signed last page are one
 * thing, and picking them together is saying so. There is no way to keep them
 * apart here — a file that should be its own document is uploaded on its own,
 * which files it as it is with no screen in between.
 *
 * So before several are saved there is one short screen: the document's name,
 * which starts as the first file's, and the files in the order their pages will
 * be in, each moved with an arrow up or down. Never by dragging: that is a rule
 * of the product, and arrows are what a thumb on a phone gets right first time.
 *
 * The server does the rest in one request — a Word file converted, everything
 * made one PDF — while this screen shows the spinner. A refusal keeps the screen
 * open with every file still on it, marks the file it was about, and the button
 * becomes "ניסיון חוזר": nothing that was picked is lost.
 */

export interface CombineItem {
  /** Stable across moves, so React keeps the picture it already has. */
  id: string;
  file: File;
  /** A photograph shows itself; a PDF or a Word file is its name. */
  url: string | null;
}

/** The picture types a browser can be trusted to draw in an `<img>`. */
const DRAWN = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

let itemSeq = 0;

/**
 * The request that files what was picked, as the two routes read it.
 *
 * One file with no name is filed as it is, under its own name and in its own
 * format, as the library always did. So is one file the pre-save screen was
 * left with after the others were taken off it (`asItIs`), under the name typed
 * there. Anything else — several files, or the pages of a scan, which always
 * come with a name — is one document, in the order given. The open folder goes
 * with either; with every document showing it is in none.
 */
export function libraryUpload(
  files: File[],
  options: { title?: string | undefined; folderId: string; asItIs?: boolean },
): { path: '/library' | '/library/combined'; form: FormData } {
  const form = new FormData();
  const [only] = files;
  const asItIs = (options.title === undefined || options.asItIs) && files.length === 1 && only ? only : null;
  if (asItIs) {
    form.append('file', asItIs, asItIs.name);
    if (options.title) form.append('title', options.title);
  } else {
    form.append('title', options.title ?? '');
    for (const file of files) form.append('files', file, file.name);
  }
  if (options.folderId) form.append('folderId', options.folderId);
  return { path: asItIs ? '/library' : '/library/combined', form };
}

/** One place earlier (-1) or later (1). A move off either end changes nothing. */
export function moveItem<T>(items: readonly T[], index: number, by: -1 | 1): T[] {
  const target = index + by;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [moved] = next.splice(index, 1);
  if (moved !== undefined) next.splice(target, 0, moved);
  return next;
}

/**
 * What a refusal says, and which file it is about.
 *
 * The server puts the words under `files` and names the file as `files.<index>`,
 * the place it had in what was sent — which is the place it has on this screen,
 * since nothing moves while the request is out. A refusal of the whole (a folder
 * gone, the document too large) names no file.
 */
export function combineRefusal(cause: unknown): { code: string; index: number | null } {
  const failure = cause instanceof RequestError ? cause : null;
  const named = Object.keys(failure?.fields ?? {})
    .map((key) => /^files\.(\d+)$/.exec(key)?.[1])
    .find((index) => index !== undefined);
  return {
    // `file` is the single upload's word, for the one file left on the screen.
    code: failure?.fields?.files ?? failure?.fields?.file ?? failure?.code ?? 'errors.unknown',
    index: named === undefined ? null : Number(named),
  };
}

/** Rounded to whichever unit keeps it to one number the reader can weigh. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const arrow =
  'h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent disabled:opacity-30 disabled:hover:bg-transparent';

/**
 * The files in the order their pages will be in: a number, a picture or an icon,
 * the name and the size, the two arrows and an X. The first cannot go up and the
 * last cannot go down, the last file left cannot be taken off (Cancel is how to
 * drop them all), and nothing changes while the save is out. A file the server
 * refused is marked, so the words above the buttons need not name it — and the
 * X is how a file that will never open is let go of without picking the rest
 * again.
 */
export function CombineList({
  items,
  busy,
  refusedIndex,
  onMove,
  onRemove,
}: {
  items: CombineItem[];
  busy: boolean;
  refusedIndex: number | null;
  onMove: (index: number, by: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <ol className="space-y-2">
      {items.map((item, index) => (
        <li
          key={item.id}
          className={cn(
            'flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors',
            refusedIndex === index ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/40',
          )}
        >
          {/* Where it stands, which is what the arrows change. */}
          <span
            aria-hidden="true"
            className="min-w-[2ch] shrink-0 text-end text-[11px] font-semibold text-muted-foreground tabular-nums ltr-nums"
          >
            {index + 1}
          </span>
          <span className="h-11 w-9 shrink-0 overflow-hidden rounded-md border border-border bg-background flex items-center justify-center">
            {item.url ? (
              <img src={item.url} alt="" className="h-full w-full object-cover" />
            ) : (
              <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
          </span>
          <span className="min-w-0 flex-1">
            {/* Isolated, so a Latin file name keeps its own order inside a Hebrew row. */}
            <span className="block truncate text-sm">
              <bdi>{item.file.name}</bdi>
            </span>
            <span className="block text-[11px] text-muted-foreground ltr-nums">{fileSize(item.file.size)}</span>
          </span>
          <button
            type="button"
            disabled={busy || index === 0}
            onClick={() => onMove(index, -1)}
            aria-label={t('common.moveUp')}
            title={t('common.moveUp')}
            className={arrow}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={busy || index === items.length - 1}
            onClick={() => onMove(index, 1)}
            aria-label={t('common.moveDown')}
            title={t('common.moveDown')}
            className={arrow}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={busy || items.length === 1}
            onClick={() => onRemove(index)}
            aria-label={t('library.combineRemove', { index: index + 1 })}
            title={t('library.combineRemove', { index: index + 1 })}
            className={cn(arrow, 'hover:text-destructive')}
          >
            <X className="h-4 w-4" />
          </button>
        </li>
      ))}
    </ol>
  );
}

export function LibraryCombineDialog({
  files,
  onSave,
  onClose,
}: {
  /** The files picked together, in the order they were picked; null while the screen is shut. */
  files: File[] | null;
  /**
   * Files them as one document under the name given, the files in this order.
   * Rejects with the server's refusal, which this screen shows.
   */
  onSave: (files: File[], title: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<CombineItem[]>([]);
  const [name, setName] = useState('');

  const save = useMutation({
    /* The rows as they stood when Save was pressed, so a refusal can be pinned to
       the file it named even after the rows are moved again. */
    mutationFn: async (sent: CombineItem[]) => {
      const ready: File[] = [];
      // A photograph goes up the size, and the right way up, that the scan's
      // own pages do (see `shrinkPicked`): the PDF writer on the other side does
      // not read a phone's orientation tag. Anything else goes as it was picked.
      for (const item of sent) ready.push(await shrinkPicked(item.file));
      await onSave(ready, name.trim());
    },
    onSuccess: () => onClose(),
  });
  const { reset } = save;

  // A new batch is a new screen: its own files, the first one's name, and no
  // refusal left over from the one before.
  useEffect(() => {
    reset();
    if (!files) {
      setItems([]);
      setName('');
      return undefined;
    }
    const next = files.map((file) => ({
      id: `combine-${(itemSeq += 1)}`,
      file,
      url: DRAWN.has(file.type) ? URL.createObjectURL(file) : null,
    }));
    setItems(next);
    setName(files[0] ? titleOf(files[0].name) : '');
    return () => {
      for (const item of next) if (item.url) URL.revokeObjectURL(item.url);
    };
  }, [files, reset]);

  const busy = save.isPending;
  const refused = save.isError ? combineRefusal(save.error) : null;
  // The server names a place in what was sent. The mark follows that file, so a
  // row moved after the refusal does not hand the mark to its neighbour.
  const refusedId = refused?.index == null ? null : (save.variables?.[refused.index]?.id ?? null);
  const refusedIndex = refusedId === null ? -1 : items.findIndex((item) => item.id === refusedId);
  // Left blank, the server names it after the file that is first by then.
  const fallback = items[0] ? titleOf(items[0].file.name) : '';

  // Nothing closes the screen while the files are on their way: what comes back
  // is either the document or the reason, and both are said here.
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Modal
      open={Boolean(files)}
      title={t('library.combineTitle')}
      onClose={close}
      footer={
        <>
          <Button type="button" variant="ghost" disabled={busy} onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={busy}
            disabled={items.length === 0}
            onClick={() => save.mutate(items)}
          >
            {refused ? t('common.retry') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('library.titleLabel')} htmlFor="combine-name">
          <Input
            id="combine-name"
            value={name}
            maxLength={160}
            disabled={busy}
            placeholder={fallback}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <p className="text-xs text-muted-foreground">{t('library.combineHint')}</p>

        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground">{t('library.combineOrder')}</p>
          <CombineList
            items={items}
            busy={busy}
            refusedIndex={refusedIndex < 0 ? null : refusedIndex}
            onMove={(index, by) => setItems((current) => moveItem(current, index, by))}
            onRemove={(index) => {
              // Taking off the file that was refused clears the refusal: what is
              // left has not been turned down, and the button is a save again.
              if (items[index]?.id === refusedId) reset();
              setItems((current) => current.filter((_, at) => at !== index));
            }}
          />
        </div>

        {/* The house spinner while the server converts and merges, said in words
            beside it, since a Word file can take a few seconds. */}
        {busy && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Spinner />
            {t('library.combining')}
          </p>
        )}
        {refused && <ErrorNotice code={refused.code} />}
      </div>
    </Modal>
  );
}
